import type {
  CompanyProfile as CompanyProfileFromGraph,
  GroupedCompanies,
  CompanyReviewsV2 as ReviewsFromGraph,
} from '@seek/ca-graphql-schema/types';
import { type Jobs, type Reviews } from '@seek/libs-shared';

import type { CompaniesListModel } from '../../../companyProfiles/models/companiesListModel';

import type {
  SearchCompanyProfileApiResult,
  SearchCompanyProfileResult,
} from './types';

export const mapReviewsModel = (
  companyProfile: CompanyProfileFromGraph,
  companyReviews: ReviewsFromGraph | undefined,
): Reviews => ({
  name: companyProfile.name,
  companyId: companyProfile.id,
  rating: companyProfile.reviewsSummary?.overallRating
    ? {
        average: companyProfile.reviewsSummary.overallRating.value,
        count:
          companyProfile.reviewsSummary.overallRating.numberOfReviews.value,
      }
    : undefined,
  reviews: companyReviews?.data?.map((r) => ({
    schemaVersion: r.schemaVersion,
    title: r.title || undefined,
    createdAt: r.createdAt?.shortAbsoluteLabel,
    pros: r.pros || undefined,
    cons: r.cons || undefined,
    overallExperienceDetails: r.overallExperienceDetails || undefined,
    overallRating: r.overallRating,
    id: r.id,
    companyId: r.companyId,
    jobTitle: r.jobTitle || undefined,
    employmentStatus: r.employmentStatus || undefined,
    workLocation: r.workLocation || undefined,
    salarySummary: r.salarySummary,
    upvotes: r.upvotes
      ? {
          count: r.upvotes.count,
          description: r.upvotes.description,
          userIds: r.upvotes.userIds ?? [],
        }
      : undefined,
    recommended: r.recommended,
    hasBeenFlagged: Boolean(r.hasBeenFlagged),
    ratings: r.ratings,
    reply: r.reply
      ? {
          companyLogoUrl: companyProfile.branding?.logo || undefined,
          createdAt: r.reply.createdAt.dateTime,
          from: r.reply.from,
          text: r.reply.text,
          user: {
            userId: r.reply.userId,
          },
        }
      : undefined,
  })),
  paging: {
    page: companyReviews?.paging?.page,
    perPage: companyReviews?.paging?.perPage,
    total: companyReviews?.paging?.total,
    totalPages: companyReviews?.paging?.totalPages,
  },
});

// Legacy Company Profile Service does the mapping for now
export const mapJobsModel = (jobs: Jobs): Jobs => jobs;

export const mapCompanyGroupsToFlatCompanies = (
  groupedCompanies?: GroupedCompanies[],
): CompaniesListModel =>
  groupedCompanies?.flatMap((group) =>
    group.companies.map((company) => ({
      name: company.name,
      slug: company.slug,
      id: company.id,
    })),
  ) ?? [];

export const mapSearchCompanyApiResultToSearchCompanyResult = (
  searchCompanyProfileApiResult?: SearchCompanyProfileApiResult,
): SearchCompanyProfileResult =>
  searchCompanyProfileApiResult?.map((companyProfile) => ({
    name: companyProfile.data.name,
    companyId: companyProfile.companyId,
    organisationId: companyProfile.organisationId,
    zone: companyProfile.zone,
    coverUrl: companyProfile.data.branding?.cover,
    logoUrl: companyProfile.data.branding?.logo,
    slug: companyProfile.data.slug,
    reviewCount: companyProfile.data.reviewCount,
    reviewRating: companyProfile.data.reviewRating,
    published: companyProfile.data.published,
  })) ?? [];
